import type React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { getProfile } from '@/api/settings';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { isInternalUser } from '@/lib/authz';

export interface CompanyLogoContextType {
  /** Signed URL for the party's company logo, or null when there is none / not applicable. */
  logoUrl: string | null;
  /** Whether the initial fetch has settled (so consumers can avoid flashing the default). */
  isLoaded: boolean;
  /**
   * Push a new logo URL into shared state (e.g. after upload/delete on Settings)
   * so the mounted sidebar swaps the logo live without a full reload.
   */
  setLogoUrl: (url: string | null) => void;
}

const CompanyLogoContext = createContext<CompanyLogoContextType | undefined>(undefined);

export const useCompanyLogo = (): CompanyLogoContextType => {
  const context = useContext(CompanyLogoContext);
  if (context === undefined) {
    throw new Error('useCompanyLogo must be used within a CompanyLogoProvider');
  }
  return context;
};

interface CompanyLogoProviderProps {
  children: React.ReactNode;
}

export const CompanyLogoProvider: React.FC<CompanyLogoProviderProps> = ({ children }) => {
  const { user, isAuthenticated } = useKeycloakAuth();
  const isInternal = isInternalUser(user?.roles);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || isInternal) {
      setLogoUrl(null);
      setIsLoaded(true);
      return;
    }
    getProfile()
      .then((profile) => {
        setLogoUrl(profile.partyInfo?.logoUrl ?? null);
      })
      .catch(() => {
        // non-critical, fall back to default logo
      })
      .finally(() => setIsLoaded(true));
  }, [isAuthenticated, isInternal]);

  const value: CompanyLogoContextType = { logoUrl, isLoaded, setLogoUrl };

  return <CompanyLogoContext.Provider value={value}>{children}</CompanyLogoContext.Provider>;
};
